/* eslint max-classes-per-file: ["error", 2] */
import { Subject } from 'rxjs';
import {
  ERR_CODE,
  FLUSH,
  RETRY_MAX,
  TIMEOUT_BLOCK_MS,
  TIMEOUT_MS,
  TYPE,
  buildFrame,
  cmdName,
  nextId,
} from './eduProtocol';

/**
 * Habla el protocolo sobre una sesión de puerto: manda REQ, espera el RSP con el
 * mismo ID, reintenta si no llega, y saca por un lado los eventos que la placa
 * manda por iniciativa propia.
 *
 * No es dueño del puerto ni sabe abrirlo: eso es de serialSession. Acá solo se
 * escribe, se escucha y se lleva la cuenta de los IDs.
 */

export class ProtocolError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = 'ProtocolError';
    this.code = code;
  }
}

export const TIMEOUT = 'TIMEOUT';

/**
 * Lo que se espera entre el relleno de ceros y el reintento. Más que los 50 ms
 * sin bytes después de los cuales la placa tira una trama a medias
 * (PROTO_RX_GAP_MS), que es lo mismo que espera el host de Python.
 */
export const FLUSH_SETTLE_MS = 120;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class EduProtocolClient {
  constructor(session, { flushSettle = FLUSH_SETTLE_MS } = {}) {
    this.session = session;
    this.flushSettle = flushSettle;
    // Los IDs arrancan en 0001: el 0000 está reservado para los eventos.
    this.lastId = 0;
    this.pending = new Map();
    this.events = new Subject();
    this.subscription = null;
  }

  start = () => {
    if (this.subscription) return;
    this.subscription = this.session.frames.subscribe(this.receive);
  }

  stop = () => {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    // Lo que quedó esperando no va a llegar nunca: mejor un rechazo ahora que una
    // promesa colgada para siempre.
    this.pending.forEach((entry) => entry.fail(new ProtocolError(TIMEOUT, 'La sesión se cerró')));
    this.pending.clear();
  }

  takeId = () => {
    this.lastId = nextId(this.lastId);
    return this.lastId;
  }

  receive = (frame) => {
    /**
     * Una trama con checksum roto se descarta en silencio y deja que el reintento
     * haga lo suyo. Contestarla como respuesta sería peor: el dato que trae no es
     * confiable, y es justo el caso para el que existen los reintentos.
     */
    if (!frame.checksumOk) return;

    if (frame.type === TYPE.EVT) {
      this.events.next(frame);
      return;
    }

    const entry = this.pending.get(frame.id);
    if (!entry) return;
    entry.receive(frame);
  }

  /**
   * Manda un REQ y espera su respuesta.
   *
   * Los reintentos van con el mismo ID, como pide la sección 8: una respuesta
   * lenta al primer intento sigue siendo válida para el segundo, y cambiar el ID
   * la volvería huérfana. Y van precedidos del relleno de ceros, que cierra lo que
   * haya quedado a medias en la placa del intento anterior.
   */
  request = (cmd, data, { timeout = TIMEOUT_MS, retries = RETRY_MAX, block = null } = {}) => {
    const id = this.takeId();
    const frameBytes = buildFrame({ type: TYPE.REQ, id, cmd, data });

    return new Promise((resolve, reject) => {
      let attemptsLeft = retries;
      let timer = null;
      let finished = false;
      // Entre el relleno y el reenvío: lo que llegue ahí no dispara otro reintento.
      let flushing = false;
      const collected = [];

      const finish = (fn, value) => {
        finished = true;
        clearTimeout(timer);
        this.pending.delete(id);
        fn(value);
      };

      const attempt = async (first) => {
        if (!first) {
          flushing = true;
          await this.session.write(FLUSH);
          await delay(this.flushSettle);
          flushing = false;
          if (finished) return;
        }
        this.session.write(frameBytes);
        timer = setTimeout(retryOrFail, timeout); // eslint-disable-line no-use-before-define
      };

      const retryOrFail = (error = new ProtocolError(TIMEOUT, `La placa no respondió ${cmdName(cmd)}`)) => {
        clearTimeout(timer);
        if (flushing) return;
        if (attemptsLeft > 1) {
          attemptsLeft -= 1;
          collected.length = 0;
          attempt(false);
          return;
        }
        finish(reject, error);
      };

      const entry = {
        fail: (error) => finish(reject, error),
        receive: (frame) => {
          if (frame.type === TYPE.ERR) {
            // En una trama ERR el código del error viaja en el campo CMD.
            const code = cmdName(frame.cmd);
            const error = new ProtocolError(code, `La placa respondió ${code}`);
            /**
             * BADCHK es nuestra trama, que le llegó rota a la placa: el pedido no
             * se procesó, y reintentarlo es lo que corresponde (§8). Cualquier otro
             * error es la respuesta al pedido y no cambia por insistir.
             */
            if (code === ERR_CODE.BADCHK) retryOrFail(error);
            else finish(reject, error);
            return;
          }
          if (!block) {
            finish(resolve, frame);
            return;
          }
          // Un bloque termina en una trama con el mismo comando y sin dato (§7);
          // lo de antes es el contenido.
          if (frame.cmd === block && frame.data.length === 0) {
            finish(resolve, collected.slice());
            return;
          }
          collected.push(frame);
        },
      };

      this.pending.set(id, entry);
      attempt(true);
    });
  }

  /**
   * Pide un bloque multi-trama (GTMA, GTAL) y devuelve las tramas de contenido,
   * sin la de cierre. El timeout es el del bloque entero y no se reintenta: son
   * diez tramas con su gap entre medio, y pedirlas de nuevo mientras todavía
   * están llegando solo agrega ruido.
   */
  requestBlock = (cmd, data, { timeout = TIMEOUT_BLOCK_MS } = {}) => this.request(cmd, data, { timeout, retries: 1, block: cmd })
}

export default EduProtocolClient;
